import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { concat, EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { accountDevicesRoute, devicesRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { DeviceRegisterApiService } from '../../device-register/services/device-register-api.service';
import { deviceRegisterAdapterActions } from '../../device-register/state/device-register.actions';
import { extensionDetailLoad } from '../../extension-details/state/extension-detail.actions';
import { extensionPhoneRegisterAdapterActions } from '../../phones/extension-phone-register/state/extension-phone-register.actions';
import { convertDeviceDetailsListItem } from '../helpers/devices-helper';
import { getDeviceDetail } from '../queries/device-detail.graphql';
import { GetDeviceDetail, GetDeviceDetailVariables } from '../queries/generated/getDeviceDetail';
import { DeviceDetailApiService } from '../services/device-detail-api.service';
import * as DeviceDetailActions from './device-detail.actions';
@Injectable()
export class DeviceDetailEffects {
   loadDeviceDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            DeviceDetailActions.deviceDetailLoad,
            DeviceDetailActions.saveDeviceDetailSuccess,
            DeviceDetailActions.deviceDetailCancel,
            DeviceDetailActions.changeSipUserPasswordSuccess
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
         concatMap(([action, params, route]) => {
            const path = route.url[0].path;
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetDeviceDetail, GetDeviceDetailVariables>({
                  query: getDeviceDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([deviceResult, privileges, route]) => {
                     const device = deviceResult.data.device;

                     const isSystemDevice = !device.account?.accountId;

                     const expectedRoute = isSystemDevice ? devicesRoute : accountDevicesRoute;

                     if (path !== expectedRoute) {
                        this.router.navigate([expectedRoute, id], {
                           replaceUrl: true,
                        });
                        return DeviceDetailActions.deviceRouteCorrected();
                     }
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageDevices_View,
                           deviceResult.data.device.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return DeviceDetailActions.loadDeviceAccessDenied();
                     }
                     if (path === devicesRoute) {
                        return DeviceDetailActions.loadSystemDeviceDetailsSuccess({
                           data: convertDeviceDetailsListItem(deviceResult.data.device),
                        });
                     } else {
                        return DeviceDetailActions.loadDeviceDetailSuccess({
                           data: convertDeviceDetailsListItem(deviceResult.data.device),
                        });
                     }
                  }),
                  catchError((error) => {
                     return of(DeviceDetailActions.loadDeviceDetailFailure(error));
                  })
               );
         })
      );
   });

   changeSipUserPassword = createEffect(() => {
      return this.actions$.pipe(
         ofType(DeviceDetailActions.changeSipUserPasswords),
         switchMap((action) => {
            return this.deviceRegisterApiService.changeSipUserPasswords(action.sipUserIds).pipe(
               map(() => {
                  return DeviceDetailActions.changeSipUserPasswordSuccess();
               }),
               catchError((error) => {
                  return of(DeviceDetailActions.changeSipUserPasswordFailure(error));
               })
            );
         })
      );
   });

   saveDeviceDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(DeviceDetailActions.saveDeviceDetail),
         switchMap((action) => {
            const apiCall = this.deviceDetailApiService.save(action.device, action.deviceFields);
            return apiCall.pipe(
               map((result) => {
                  return DeviceDetailActions.saveDeviceDetailSuccess({ data: result.data });
               })
            );
         })
      );
   });

   deleteDevice = createEffect(() => {
      return this.actions$.pipe(
         ofType(DeviceDetailActions.deleteDevice),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Device?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Device?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return concat(
                        of(DeviceDetailActions.confirmDeviceChange()),
                        this.deviceRegisterApiService.resetPhone(action.deviceId!).pipe(
                           catchError((error) => {
                              console.error('resetPhone failed:', error);
                              return of(null);
                           }),
                           switchMap(() => {
                              return this.deviceDetailApiService.deleteDevice(action.deviceId).pipe(
                                 map(() => DeviceDetailActions.deleteDeviceSuccess(action)),
                                 catchError((error) => of(DeviceDetailActions.deleteDeviceFailure(error)))
                              );
                           })
                        )
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteDeviceSuccess = createEffect(() =>
      this.actions$.pipe(
         ofType(DeviceDetailActions.deleteDeviceSuccess),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute)), this.store.pipe(select(getSelectedAccountId))),
         concatMap(([action, route, selectedAccountId]) => {
            this.snackBar.open(`Device ${action.deviceId} deleted`);

            const fullPath = route.url.map((segment: any) => segment.path).join('/');

            const isDeviceDetail = [`${devicesRoute}/`, `${accountDevicesRoute}/`].some((prefix) =>
               fullPath.startsWith(prefix)
            );

            if (isDeviceDetail) {
               const navigateTo = selectedAccountId ? `/${accountDevicesRoute}` : `/${devicesRoute}`;
               this.router.navigate([navigateTo]);
               return EMPTY;
            }

            if (fullPath === devicesRoute || fullPath === accountDevicesRoute) {
               return of(deviceRegisterAdapterActions.pageEnter());
            }

            if (fullPath.startsWith('extensions')) {
               return of(extensionPhoneRegisterAdapterActions.pageEnter());
            }

            return EMPTY;
         })
      )
   );

   releaseDeviceDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(DeviceDetailActions.releaseDeviceDetail),
         switchMap((action) => {
            const message = 'Are you sure you would like to release the device from the current account?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Release Device?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return concat(
                        of(DeviceDetailActions.confirmDeviceChange()),
                        this.deviceRegisterApiService.resetPhone(action.device.deviceId!).pipe(
                           catchError((error) => {
                              console.error('resetPhone failed:', error);
                              return of(null);
                           }),
                           switchMap(() => {
                              return this.deviceDetailApiService.save(action.device, action.deviceFields).pipe(
                                 map((result) => DeviceDetailActions.releaseDeviceDetailSuccess({ data: result.data })),
                                 catchError((error) => of(DeviceDetailActions.releaseDeviceDetailFailure(error)))
                              );
                           })
                        )
                     );
                  }

                  return EMPTY;
               })
            );
         })
      );
   });

   saveDeviceDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(DeviceDetailActions.saveDeviceDetailSuccess),
            tap(() => {
               this.snackBar.open('Device Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   releaseDeviceDetailSuccess = createEffect(() =>
      this.actions$.pipe(
         ofType(DeviceDetailActions.releaseDeviceDetailSuccess),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute)), this.store.pipe(select(getSelectedAccountId))),
         concatMap(([action, route, selectedAccountId]) => {
            this.snackBar.open(`Device ${action.data.device.macAddress} Released`);

            const fullPath = route.url.map((segment: any) => segment.path).join('/');

            const isDeviceDetail = [`${devicesRoute}/`, `${accountDevicesRoute}/`].some((prefix) =>
               fullPath.startsWith(prefix)
            );

            if (isDeviceDetail) {
               const navigateTo = selectedAccountId ? `/${accountDevicesRoute}` : `/${devicesRoute}`;
               this.router.navigate([navigateTo]);
               return EMPTY;
            }

            if (fullPath === devicesRoute || fullPath === accountDevicesRoute) {
               return of(deviceRegisterAdapterActions.pageEnter());
            }

            if (fullPath.startsWith('extensions')) {
               return of(extensionDetailLoad());
            }

            return EMPTY;
         })
      )
   );

   releaseDeviceToAccount = createEffect(() => {
      return this.actions$.pipe(
         ofType(DeviceDetailActions.releaseDeviceToAccount),
         withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
         switchMap(([action, accountId]) => {
            if (!accountId) {
               return EMPTY;
            }
            const message =
               'Are you sure you would like to reset this device on the account? This will clear its data but keep it assigned to this account.';
            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reset Device on Account?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return concat(
                        of(DeviceDetailActions.confirmDeviceChange()),
                        this.deviceRegisterApiService.resetPhone(action.deviceId!).pipe(
                           catchError((error) => {
                              console.error('resetPhone failed:', error);
                              return of(null);
                           }),
                           switchMap(() => {
                              const releasePayload = { deviceId: action.deviceId, accountId: null };
                              return this.deviceDetailApiService.save(releasePayload, ['accountId']).pipe(
                                 switchMap(() => {
                                    const assignPayload = { deviceId: action.deviceId, accountId };
                                    return this.deviceDetailApiService.save(assignPayload, ['accountId']);
                                 }),
                                 map((result) =>
                                    DeviceDetailActions.releaseDeviceToAccountSuccess({ data: result.data })
                                 ),
                                 catchError((error) => of(DeviceDetailActions.releaseDeviceToAccountFailure(error)))
                              );
                           })
                        )
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   releaseDeviceToAccountSuccess = createEffect(() =>
      this.actions$.pipe(
         ofType(DeviceDetailActions.releaseDeviceToAccountSuccess),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute))),
         concatMap(([action, route]) => {
            this.snackBar.open('Device has been reset on this account.');

            const fullPath = route.url.map((segment: any) => segment.path).join('/');

            if (fullPath === devicesRoute || fullPath === accountDevicesRoute) {
               return of(deviceRegisterAdapterActions.pageEnter());
            }

            if (fullPath.startsWith('extensions')) {
               return of(extensionDetailLoad());
            }

            return EMPTY;
         })
      )
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private deviceDetailApiService: DeviceDetailApiService,
      private deviceRegisterApiService: DeviceRegisterApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location,
      private dialogService: LcsDialogService
   ) {}
}
